import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  getWalletCustomer,
  getWalletTransactions,
} from "../../../utils/APICalls/walletAPICalls";
import {
  parseWalletSummary,
  parseWalletTransactionsPage,
} from "../../../utils/wallet/walletParsers";
import type { AppDispatch } from "../app/store";

export type WalletFetchArg = { restaurantId: number };

/** How many entries the Wallet page's "Latest Transactions" shows. */
const LATEST_WALLET_TRANSACTIONS_LIMIT = 5;

/**
 * The backend's answer for a customer who never topped up: the wallet service
 * only opens a wallet on the first credit (prepits/prepareit-backend#607).
 */
const CUSTOMER_WALLET_NOT_FOUND = "ProxyWalletService_CustomerWalletNotFound";

/**
 * Resolves `null` for "this customer has no wallet", so the slice can tell it
 * apart from an outage -- which still rejects, keeping the last balance seen.
 * The inactive wallet's visibility turns on that difference (#4259).
 */
export const fetchWalletSummary = createAsyncThunk(
  "wallet/fetchSummary",
  async ({ restaurantId }: WalletFetchArg) => {
    try {
      const res = await getWalletCustomer(restaurantId);

      return parseWalletSummary(res.data.data);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.data?.error === CUSTOMER_WALLET_NOT_FOUND
      ) {
        return null;
      }
      throw error;
    }
  }
);

export const fetchLatestWalletTransactions = createAsyncThunk(
  "wallet/fetchLatestTransactions",
  async ({ restaurantId }: WalletFetchArg) => {
    const res = await getWalletTransactions({
      restaurantId,
      limit: LATEST_WALLET_TRANSACTIONS_LIMIT,
    });

    return parseWalletTransactionsPage(res.data.data).items;
  }
);

/**
 * Balance and latest transactions together. The wallet emits no socket event,
 * so screens call this whenever the balance may have moved (back from a
 * top-up, an order placed with the wallet).
 */
export const refreshWalletCustomerData =
  (arg: WalletFetchArg) => (dispatch: AppDispatch) => {
    dispatch(fetchWalletSummary(arg));
    dispatch(fetchLatestWalletTransactions(arg));
  };
